/**
 * Transmissions: what a signal actually sounds like.
 *
 * A content pack names a source and the engine builds it — `{ "type": "synth", "preset":
 * "morse" }` or `{ "type": "audio", ... }`. Authors never touch this file; adding a signal is a
 * data change.
 *
 * Most of these are generative on purpose (locked decision: non-speech signal and parasite FX
 * are client-side generative where possible). A pack of twenty parasite signals costs twenty
 * JSON objects and no download, and each one is subtly different every time it is heard, which
 * is what keeps a band from sounding like a playlist.
 *
 * Everything here is *pre-filter*: the receiver runs the output through the tuning chain, so a
 * transmission fades in through the filters rather than through a volume knob.
 */
import { audioContext } from './context.js';
import type { SignalSource } from '../world/matrix.js';

export interface Transmission {
  readonly output: AudioNode;
  stop(): void;
}

/** A preset builds its voice into `output` and returns whatever needs stopping. */
type PresetBuilder = (
  ctx: BaseAudioContext,
  output: GainNode,
  params: Record<string, number>,
) => Array<() => void>;

function param(params: Record<string, number>, key: string, fallback: number): number {
  const value = params[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/** A carrier that fades on a slow LFO — the sound of propagation, not of a synth effect. */
function buildCarrier(
  ctx: BaseAudioContext,
  output: GainNode,
  frequencyHz: number,
  level: number,
  fadeHz = 0.13,
): Array<() => void> {
  const carrier = ctx.createOscillator();
  carrier.type = 'sine';
  carrier.frequency.value = frequencyHz;

  const gain = ctx.createGain();
  gain.gain.value = level;
  carrier.connect(gain).connect(output);
  carrier.start();

  const fade = ctx.createOscillator();
  fade.type = 'sine';
  fade.frequency.value = fadeHz;
  const depth = ctx.createGain();
  depth.gain.value = level * 0.5;
  fade.connect(depth).connect(gain.gain);
  fade.start();

  return [() => carrier.stop(), () => fade.stop()];
}

/** One gated tone. The ramps matter: a hard gate on a square wave clicks. */
function scheduleTone(
  ctx: BaseAudioContext,
  output: GainNode,
  options: {
    at: number;
    seconds: number;
    frequencyHz: number;
    level: number;
    type?: OscillatorType;
  },
): OscillatorNode {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = options.type ?? 'square';
  osc.frequency.value = options.frequencyHz;

  const edge = Math.min(0.03, options.seconds / 4);
  gain.gain.setValueAtTime(0, options.at);
  gain.gain.linearRampToValueAtTime(options.level, options.at + edge);
  gain.gain.setValueAtTime(options.level, options.at + options.seconds - edge);
  gain.gain.linearRampToValueAtTime(0, options.at + options.seconds);

  osc.connect(gain).connect(output);
  osc.start(options.at);
  osc.stop(options.at + options.seconds + 0.02);
  return osc;
}

/** Shared machinery for presets that repeat a phrase forever. */
function repeating(
  ctx: BaseAudioContext,
  playPhrase: (startAt: number) => number,
  restSeconds: number,
): Array<() => void> {
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const run = (): void => {
    if (stopped) return;
    const phraseSeconds = playPhrase(ctx.currentTime + 0.05);
    timer = setTimeout(run, (phraseSeconds + restSeconds) * 1000);
  };
  run();

  return [
    () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    },
  ];
}

const MORSE: Record<string, string> = {
  n: '-.',
  i: '..',
  g: '--.',
  h: '....',
  t: '-',
  b: '-...',
  a: '.-',
  d: '-..',
};

const PRESETS: Record<string, PresetBuilder> = {
  /** A dead carrier. The most common thing on a real band, and the least interesting — by design. */
  hum: (ctx, output, params) =>
    buildCarrier(ctx, output, param(params, 'toneHz', 96), param(params, 'level', 0.16), 0.07),

  /** Numbers station: carrier plus a slightly wrong little tune, repeating with a rest. */
  numbers: (ctx, output, params) => {
    const stops = buildCarrier(ctx, output, param(params, 'carrierHz', 110), 0.14);
    const motif = [523.25, 392.0, 659.25, 440.0];
    const toneSeconds = param(params, 'toneSeconds', 0.55);
    const gapSeconds = param(params, 'gapSeconds', 0.25);
    const voices: OscillatorNode[] = [];

    stops.push(
      ...repeating(
        ctx,
        (startAt) => {
          motif.forEach((frequencyHz, index) => {
            voices.push(
              scheduleTone(ctx, output, {
                at: startAt + index * (toneSeconds + gapSeconds),
                seconds: toneSeconds,
                frequencyHz,
                level: 0.22,
              }),
            );
          });
          return motif.length * (toneSeconds + gapSeconds);
        },
        param(params, 'restSeconds', 2.4),
      ),
      () => {
        for (const voice of voices) {
          try {
            voice.stop();
          } catch {
            /* already ended */
          }
        }
      },
    );
    return stops;
  },

  /** A navigation beacon: one short tone, over and over, patient and inhuman. */
  beacon: (ctx, output, params) => {
    const toneHz = param(params, 'toneHz', 660);
    const periodMs = param(params, 'periodMs', 2400);
    const pulseSeconds = param(params, 'pulseSeconds', 0.18);
    const voices: OscillatorNode[] = [];

    return [
      ...repeating(
        ctx,
        (startAt) => {
          voices.push(
            scheduleTone(ctx, output, {
              at: startAt,
              seconds: pulseSeconds,
              frequencyHz: toneHz,
              level: 0.2,
              type: 'sine',
            }),
          );
          return pulseSeconds;
        },
        Math.max(periodMs / 1000 - pulseSeconds, 0.2),
      ),
      () => {
        for (const voice of voices) {
          try {
            voice.stop();
          } catch {
            /* already ended */
          }
        }
      },
    ];
  },

  /** Morse. Timing is real: a dot is 1.2/wpm seconds, and everything else follows from it. */
  morse: (ctx, output, params) => {
    const wpm = param(params, 'wpm', 14);
    const dot = 1.2 / wpm;
    const toneHz = param(params, 'toneHz', 700);
    const text = 'nightband';
    const voices: OscillatorNode[] = [];

    return [
      ...repeating(
        ctx,
        (startAt) => {
          let cursor = startAt;
          for (const letter of text) {
            const code = MORSE[letter];
            if (!code) {
              cursor += dot * 7; // word gap for anything unmapped
              continue;
            }
            for (const symbol of code) {
              const seconds = symbol === '-' ? dot * 3 : dot;
              voices.push(
                scheduleTone(ctx, output, {
                  at: cursor,
                  seconds,
                  frequencyHz: toneHz,
                  level: 0.18,
                  type: 'sine',
                }),
              );
              cursor += seconds + dot;
            }
            cursor += dot * 2; // letter gap (3 dots total, one already added)
          }
          return cursor - startAt;
        },
        param(params, 'restSeconds', 3),
      ),
      () => {
        for (const voice of voices) {
          try {
            voice.stop();
          } catch {
            /* already ended */
          }
        }
      },
    ];
  },

  /**
   * Someone talking, too far away to make out. A carrier modulated by a wandering envelope at
   * roughly syllable rate — the ear reads it as speech long before it reads it as a synth.
   */
  'voice-carrier': (ctx, output, params) => {
    const toneHz = param(params, 'toneHz', 220);
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.value = toneHz;

    const formant = ctx.createBiquadFilter();
    formant.type = 'bandpass';
    formant.frequency.value = 900;
    formant.Q.value = 4;

    const envelope = ctx.createGain();
    envelope.gain.value = 0.04;

    osc.connect(formant).connect(envelope).connect(output);
    osc.start();

    // Syllables: a fast LFO gates the envelope, a slow one moves the formant.
    const syllable = ctx.createOscillator();
    syllable.type = 'triangle';
    syllable.frequency.value = param(params, 'syllableHz', 3.4);
    const syllableDepth = ctx.createGain();
    syllableDepth.gain.value = 0.035;
    syllable.connect(syllableDepth).connect(envelope.gain);
    syllable.start();

    const shape = ctx.createOscillator();
    shape.type = 'sine';
    shape.frequency.value = 0.23;
    const shapeDepth = ctx.createGain();
    shapeDepth.gain.value = 400;
    shape.connect(shapeDepth).connect(formant.frequency);
    shape.start();

    return [() => osc.stop(), () => syllable.stop(), () => shape.stop()];
  },

  /** Breath with a pitch in it. Bursts of filtered noise, irregular enough to feel intentional. */
  whisper: (ctx, output, params) => {
    const length = Math.floor(ctx.sampleRate * 2);
    const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i += 1) data[i] = Math.random() * 2 - 1;

    const noise = ctx.createBufferSource();
    noise.buffer = buffer;
    noise.loop = true;

    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = param(params, 'toneHz', 320);
    band.Q.value = 2.5;

    const envelope = ctx.createGain();
    envelope.gain.value = 0;

    noise.connect(band).connect(envelope).connect(output);
    noise.start();

    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const breathe = (): void => {
      if (stopped) return;
      const now = ctx.currentTime;
      const seconds = 0.6 + Math.random() * 1.4;
      envelope.gain.cancelScheduledValues(now);
      envelope.gain.setValueAtTime(envelope.gain.value, now);
      envelope.gain.linearRampToValueAtTime(0.09, now + seconds * 0.4);
      envelope.gain.linearRampToValueAtTime(0, now + seconds);
      timer = setTimeout(breathe, (seconds + 0.4 + Math.random() * 2.5) * 1000);
    };
    breathe();

    return [
      () => noise.stop(),
      () => {
        stopped = true;
        if (timer) clearTimeout(timer);
      },
    ];
  },

  /**
   * The buzzer: a channel that has been holding one abrasive tone for years.
   *
   * Modelled on the real thing (UVB-76 and its relatives). What makes it unnerving is not the
   * sound but the *commitment* — nobody does this by accident, so the player is left with the
   * question of who is maintaining it and why. A red herring that rewards attention with
   * nothing is still doing work: it teaches that not everything on the band is for you.
   */
  buzzer: (ctx, output, params) => {
    const toneHz = param(params, 'toneHz', 92);
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.value = toneHz;

    const shape = ctx.createBiquadFilter();
    shape.type = 'lowpass';
    shape.frequency.value = 1400;
    shape.Q.value = 0.8;

    const envelope = ctx.createGain();
    envelope.gain.value = 0;
    osc.connect(shape).connect(envelope).connect(output);
    osc.start();

    // A repeating on/off at roughly a second, ramped at both ends — a hard gate on a sawtooth is
    // a click, and a click reads as a bug rather than as a transmitter.
    const gate = ctx.createOscillator();
    gate.type = 'square';
    gate.frequency.value = param(params, 'gateHz', 1.15);
    const gateShape = ctx.createBiquadFilter();
    gateShape.type = 'lowpass';
    gateShape.frequency.value = 22;
    const gateDepth = ctx.createGain();
    gateDepth.gain.value = 0.035;
    gate.connect(gateShape).connect(gateDepth).connect(envelope.gain);
    gate.start();

    const bias = ctx.createConstantSource();
    bias.offset.value = 0.038;
    bias.connect(envelope.gain);
    bias.start();

    return [() => osc.stop(), () => gate.stop(), () => bias.stop()];
  },

  /**
   * Digital traffic: the chirp of something machine-readable that is not for the player.
   *
   * Two tones alternating fast, in irregular-length bursts, like RTTY or packet. Deliberately
   * unlistenable — its whole job is to be *obviously* not a voice, so a player learns to
   * recognise the shape of a dead end in a second and a half rather than sitting through it.
   */
  databurst: (ctx, output, params) => {
    const markHz = param(params, 'toneHz', 1275);
    const shiftHz = param(params, 'shiftHz', 170);

    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = markHz;

    const envelope = ctx.createGain();
    envelope.gain.value = 0;
    osc.connect(envelope).connect(output);
    osc.start();

    // The keying: a square LFO swinging the carrier between mark and space.
    const keying = ctx.createOscillator();
    keying.type = 'square';
    keying.frequency.value = param(params, 'baudHz', 45);
    const keyingDepth = ctx.createGain();
    keyingDepth.gain.value = shiftHz / 2;
    keying.connect(keyingDepth).connect(osc.frequency);
    keying.start();

    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const burst = (): void => {
      if (stopped) return;
      const now = ctx.currentTime;
      const seconds = 0.4 + Math.random() * 2.2;
      envelope.gain.cancelScheduledValues(now);
      envelope.gain.setValueAtTime(envelope.gain.value, now);
      envelope.gain.linearRampToValueAtTime(0.05, now + 0.02);
      envelope.gain.setValueAtTime(0.05, now + seconds);
      envelope.gain.linearRampToValueAtTime(0, now + seconds + 0.02);
      timer = setTimeout(burst, (seconds + 0.5 + Math.random() * 3.5) * 1000);
    };
    burst();

    return [
      () => osc.stop(),
      () => keying.stop(),
      () => {
        stopped = true;
        if (timer) clearTimeout(timer);
      },
    ];
  },

  /**
   * An over-the-horizon radar sweeping the band: the "woodpecker".
   *
   * A hard pulse train that walks slowly across the dial. This one is texture with teeth — it is
   * the only preset that sounds *aimed*, and a player who notices it moving has learned that the
   * band has weather in it as well as people.
   */
  woodpecker: (ctx, output, params) => {
    const length = Math.floor(ctx.sampleRate * 0.5);
    const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i += 1) data[i] = Math.random() * 2 - 1;

    const noise = ctx.createBufferSource();
    noise.buffer = buffer;
    noise.loop = true;

    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = param(params, 'toneHz', 1100);
    band.Q.value = 1.6;

    const envelope = ctx.createGain();
    envelope.gain.value = 0;
    noise.connect(band).connect(envelope).connect(output);
    noise.start();

    // ~10 pulses a second, the rate the real one ran at. Shaped by a low-pass on the square so
    // the pulse has an edge without a click.
    const pulse = ctx.createOscillator();
    pulse.type = 'square';
    pulse.frequency.value = param(params, 'pulseHz', 10);
    const pulseShape = ctx.createBiquadFilter();
    pulseShape.type = 'lowpass';
    pulseShape.frequency.value = 90;
    const pulseDepth = ctx.createGain();
    pulseDepth.gain.value = 0.05;
    pulse.connect(pulseShape).connect(pulseDepth).connect(envelope.gain);
    pulse.start();

    const bias = ctx.createConstantSource();
    bias.offset.value = 0.05;
    bias.connect(envelope.gain);
    bias.start();

    // The sweep: the passband walks up and down over half a minute.
    const sweep = ctx.createOscillator();
    sweep.type = 'triangle';
    sweep.frequency.value = 0.031;
    const sweepDepth = ctx.createGain();
    sweepDepth.gain.value = 420;
    sweep.connect(sweepDepth).connect(band.frequency);
    sweep.start();

    return [() => noise.stop(), () => pulse.stop(), () => bias.stop(), () => sweep.stop()];
  },

  /**
   * A time signal: ticks with a long tone on the minute.
   *
   * The most reassuring thing on the band and, in context, one of the worst. It is proof that
   * something automated is still running exactly on schedule, with nobody listening.
   */
  timesignal: (ctx, output, params) => {
    const tickHz = param(params, 'toneHz', 1000);
    const stops: Array<() => void> = [];
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = (long: boolean): void => {
      if (stopped) return;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = long ? tickHz / 2 : tickHz;
      const now = ctx.currentTime;
      const seconds = long ? 0.55 : 0.045;
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.06, now + 0.004);
      gain.gain.setValueAtTime(0.06, now + seconds);
      gain.gain.linearRampToValueAtTime(0, now + seconds + 0.01);
      osc.connect(gain).connect(output);
      osc.start(now);
      osc.stop(now + seconds + 0.05);
      osc.onended = () => {
        osc.disconnect();
        gain.disconnect();
      };
    };

    // Nine short ticks then a long one: a minute marker, counted rather than clocked, so it needs
    // no wall-clock reading and stays in step with itself.
    let count = 0;
    const run = (): void => {
      if (stopped) return;
      count = (count + 1) % 10;
      tick(count === 0);
      timer = setTimeout(run, 1000);
    };
    run();

    stops.push(() => {
      stopped = true;
      if (timer) clearTimeout(timer);
    });
    return stops;
  },

  /**
   * Something mechanical turning over, far away.
   *
   * Very low, very slow, almost sub-audible. Not a transmitter at all — the sound a receiver
   * makes when it is picking up machinery through the ground. Put one at the bottom of the band
   * and the dial acquires a basement.
   */
  machinery: (ctx, output, params) => {
    const baseHz = param(params, 'toneHz', 47);
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = baseHz;

    const grit = ctx.createBiquadFilter();
    grit.type = 'lowpass';
    grit.frequency.value = 260;

    const envelope = ctx.createGain();
    envelope.gain.value = 0.07;
    osc.connect(grit).connect(envelope).connect(output);
    osc.start();

    // A slow wobble in pitch: something turning slightly out of true.
    const wobble = ctx.createOscillator();
    wobble.type = 'sine';
    wobble.frequency.value = param(params, 'wobbleHz', 0.42);
    const wobbleDepth = ctx.createGain();
    wobbleDepth.gain.value = baseHz * 0.06;
    wobble.connect(wobbleDepth).connect(osc.frequency);
    wobble.start();

    // ...and a slower one in level, so it never settles into a drone.
    const breathe = ctx.createOscillator();
    breathe.type = 'sine';
    breathe.frequency.value = 0.09;
    const breatheDepth = ctx.createGain();
    breatheDepth.gain.value = 0.03;
    breathe.connect(breatheDepth).connect(envelope.gain);
    breathe.start();

    return [() => osc.stop(), () => wobble.stop(), () => breathe.stop()];
  },
};

export const TRANSMISSION_PRESETS = Object.keys(PRESETS);

/** Pre-rendered speech (Chapter 3 onward). Fetch, decode, play; loop if the pack says so. */
function buildAudioTransmission(
  ctx: BaseAudioContext,
  output: GainNode,
  url: string,
  loop: boolean,
): Array<() => void> {
  let source: AudioBufferSourceNode | null = null;
  let cancelled = false;

  void fetch(url)
    .then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.arrayBuffer();
    })
    .then((bytes) => ctx.decodeAudioData(bytes))
    .then((buffer) => {
      if (cancelled) return;
      source = ctx.createBufferSource();
      source.buffer = buffer;
      source.loop = loop;
      source.connect(output);
      source.start();
    })
    .catch((error: unknown) => {
      // A missing asset must not take the receiver down: the band simply carries no voice here.
      // The pack validator is what catches this before a player ever does.
      console.warn(`[nightband] transmission audio unavailable: ${url}`, error);
    });

  return [
    () => {
      cancelled = true;
      try {
        source?.stop();
      } catch {
        /* never started */
      }
    },
  ];
}

/** Build the transmission a signal's source describes. */

/**
 * Per-preset output trim, so the band is not a rollercoaster.
 *
 * Measured, not guessed. Every preset was rendered through an `OfflineAudioContext` and its
 * loudest 50 ms window compared — peak window rather than average, because duty cycle must not
 * count: a beacon is silent 96% of the time and a hum never is, but a listener judges both by how
 * loud they are *while sounding*.
 *
 * Before this table the spread was **32 dB**, from `numbers` at +8 to `whisper` at −24, and
 * `voice-carrier` — the default voice of every NPC — sat 19 dB below a plain carrier. Sweeping
 * the dial meant lunging for the volume knob, and the quietest signals were simply lost under the
 * static they were supposed to be competing with.
 *
 * The trims below land each preset on an *intended* level rather than on a flat one. Signals a
 * player has to understand sit at reference; texture sits below it, by an amount chosen for what
 * the thing is rather than for what it happened to measure.
 *
 * ## The absolute level is set against the static, not chosen
 *
 * The first version of this table matched the presets to each other perfectly and put them all
 * 6 dB too low, because the reference was picked in isolation. Measured through the mixer, tuning
 * *into* a signal made the output quieter than dead air: the static ducks to 28% under a lock, and
 * the transmission was not clearing what was left. Atmosphere at the cost of intelligibility is
 * not a trade this chapter is allowed to make, so reference level is now fixed by the requirement
 * that a locked signal reads at least twice the ducked noise floor.
 *
 * Re-measure with the `preset-check` harness after touching any preset, and re-run `mixer-check`
 * — the first proves they still match each other, the second that they still clear the static.
 */
const PRESET_TRIM: Record<string, number> = {
  // Reference level — things the player is meant to hear and identify.
  hum: 1.04,
  numbers: 0.76,
  beacon: 1.28,
  morse: 1.42,
  // The default NPC voice. Was the quietest thing on the band; now the clearest, because a
  // conversation the player cannot hear is the one failure this chapter cannot ship with.
  'voice-carrier': 17.7,

  // Texture, deliberately under reference.
  whisper: 15.4, //  −6 dB: faint by design, but faint is not inaudible.
  buzzer: 4.08, //   −2 dB: abrasive enough without being the loudest thing on the dial.
  databurst: 3.6, //  −3 dB: harsh, and should not reward stopping on it.
  woodpecker: 10.0, //−3 dB: aggressive, but it is weather rather than a message.
  timesignal: 2.82, //−4 dB: precise and small.
  machinery: 1.68, //−5 dB: felt through the floor more than heard.
};

export function createTransmission(source: SignalSource): Transmission {
  return createTransmissionIn(audioContext(), source);
}

/**
 * Build a transmission against a specific context.
 *
 * Split out from `createTransmission` so a preset can be rendered into an `OfflineAudioContext`
 * and *measured* — silence, clipping and DC offset are content bugs that no unit test can see,
 * because these are audio graphs rather than functions. The harness that checks every preset
 * sounds sane uses this; nothing in the game does anything but pass the shared context.
 */
export function createTransmissionIn(ctx: BaseAudioContext, source: SignalSource): Transmission {
  const output = ctx.createGain();
  // Pre-rendered audio is authored at a level Deshret controls, so only synth presets are trimmed.
  output.gain.value = source.type === 'synth' ? (PRESET_TRIM[source.preset] ?? 1) : 1;

  let stops: Array<() => void>;
  if (source.type === 'audio') {
    stops = buildAudioTransmission(ctx, output, source.url, source.loop ?? false);
  } else {
    const builder = PRESETS[source.preset];
    if (builder) {
      stops = builder(ctx, output, source.params ?? {});
    } else {
      // Unknown preset: fall back to a carrier rather than crashing the world. Content is data,
      // and a typo in data should degrade to "something is transmitting here", not to a black
      // screen. This should be unreachable in practice — the validator rejects an unknown preset
      // at authoring time (Chapter 7) — so it is a backstop for a pack that got here another way.
      console.warn(`[nightband] unknown transmission preset "${source.preset}" — using a carrier`);
      stops = PRESETS.hum!(ctx, output, {});
    }
  }

  let stopped = false;
  return {
    output,
    stop: () => {
      if (stopped) return;
      stopped = true;
      for (const stop of stops) {
        try {
          stop();
        } catch {
          /* already stopped */
        }
      }
      output.disconnect();
    },
  };
}
